/**
 * node-capability-registry.ts
 *
 * Node Capability Registry, Hardware Capacity Bounds, 7-State Lifecycle FSM,
 * Strict Recovery Gate (consecutive healthy pings), and Ramp-Up Concurrency Multipliers.
 */

import {
  NodeCapability,
  NodeHeartbeat,
  NodeLifecycleState,
  NodeRuntimePolicy,
  ModelKind,
  HeartbeatPolicy,
} from '../contracts/model-orchestration-contracts.js';
import { OrchestrationStore } from '../storage/orchestration-store.js';

export const DEFAULT_HEARTBEAT_POLICY: HeartbeatPolicy = {
  intervalMs: 3000,
  degradedAfterMissed: 2,
  offlineAfterMissed: 5,
  recoveryConfirmationCount: 2,
};

export class NodeCapabilityRegistry {
  private consecutiveMissedPings = new Map<string, number>(); // nodeId -> missed count
  private consecutiveHealthyPings = new Map<string, number>(); // nodeId -> healthy count

  constructor(
    private readonly store: OrchestrationStore,
    private readonly heartbeatPolicy: HeartbeatPolicy = DEFAULT_HEARTBEAT_POLICY
  ) {}

  async registerNode(capability: NodeCapability): Promise<void> {
    await this.store.saveNodeCapability(capability);
    this.consecutiveMissedPings.set(capability.nodeId, 0);
    this.consecutiveHealthyPings.set(capability.nodeId, capability.lifecycleState === 'ONLINE' ? 1 : 0);

    await this.store.appendEvent({
      eventId: `evt-node-reg-${Date.now()}`,
      kind: 'node.online',
      payload: { nodeId: capability.nodeId, name: capability.nodeName, roles: capability.roles },
      originId: 'registry',
      sequence: 0,
      occurredAt: new Date().toISOString(),
    });
  }

  async getNode(nodeId: string): Promise<NodeCapability | undefined> {
    const nodes = await this.store.getNodeCapabilities();
    return nodes.find((n) => n.nodeId === nodeId);
  }

  async getAllNodes(): Promise<readonly NodeCapability[]> {
    return this.store.getNodeCapabilities();
  }

  async getPolicy(nodeId: string): Promise<NodeRuntimePolicy> {
    const node = await this.getNode(nodeId);
    if (node) return node.policy;

    return {
      disabled: false,
      quarantined: false,
      draining: false,
      allowMasterFallback: true,
      maxNeedleInstances: 4,
      maxActiveLlmRuntimes: 1,
      allowMixedLlmModels: false,
      recoveryRampUpPhases: [
        { durationMs: 10000, capacityPercent: 25 },
        { durationMs: 10000, capacityPercent: 50 },
        { durationMs: 'infinite', capacityPercent: 100 },
      ],
    };
  }

  /**
   * Ingests a signed node heartbeat and advances the lifecycle state machine.
   */
  async ingestHeartbeat(heartbeat: NodeHeartbeat, now: number = Date.now()): Promise<NodeCapability | undefined> {
    const node = await this.getNode(heartbeat.nodeId);
    if (!node) return undefined;

    // Reset missed pings
    this.consecutiveMissedPings.set(node.nodeId, 0);

    let nextState: NodeLifecycleState = node.lifecycleState;
    let recoveryStartedAt = node.recoveryStartedAt;
    let healthyCount = (this.consecutiveHealthyPings.get(node.nodeId) || 0) + 1;
    this.consecutiveHealthyPings.set(node.nodeId, healthyCount);

    if (node.lifecycleState === 'REGISTERED') {
      nextState = 'ONLINE';
      await this.store.appendEvent({
        eventId: `evt-online-${Date.now()}`,
        kind: 'node.online',
        payload: { nodeId: node.nodeId },
        originId: 'heartbeat',
        sequence: 0,
        occurredAt: new Date(now).toISOString(),
      });
    } else if (node.lifecycleState === 'DEGRADED') {
      nextState = 'ONLINE';
    } else if (node.lifecycleState === 'OFFLINE') {
      // First healthy heartbeat moves to RECOVERING
      nextState = 'RECOVERING';
      healthyCount = 1;
      this.consecutiveHealthyPings.set(node.nodeId, healthyCount);
      recoveryStartedAt = new Date(now).toISOString();

      await this.store.appendEvent({
        eventId: `evt-recov-start-${Date.now()}`,
        kind: 'node.rampup.started',
        payload: { nodeId: node.nodeId },
        originId: 'heartbeat',
        sequence: 0,
        occurredAt: new Date(now).toISOString(),
      });
    } else if (node.lifecycleState === 'RECOVERING') {
      if (healthyCount >= this.heartbeatPolicy.recoveryConfirmationCount) {
        nextState = 'ONLINE';
        await this.store.appendEvent({
          eventId: `evt-recov-done-${Date.now()}`,
          kind: 'node.recovered',
          payload: { nodeId: node.nodeId },
          originId: 'heartbeat',
          sequence: 0,
          occurredAt: new Date(now).toISOString(),
        });
      }
    }

    const updated: NodeCapability = {
      ...node,
      capacity: heartbeat.capacity,
      lastHeartbeatAt: new Date(now).toISOString(),
      lifecycleState: nextState,
      recoveryStartedAt,
    };

    await this.store.saveNodeCapability(updated);
    await this.store.appendHeartbeat({
      ...heartbeat,
      lifecycleState: nextState,
      consecutiveHealthyCount: healthyCount,
    });

    return updated;
  }

  /**
   * Records a missed heartbeat interval (called by heartbeat watchdog or simulation).
   */
  async recordMissedHeartbeat(nodeId: string, now: number = Date.now()): Promise<NodeCapability | undefined> {
    const node = await this.getNode(nodeId);
    if (!node) return undefined;

    const missed = (this.consecutiveMissedPings.get(nodeId) || 0) + 1;
    this.consecutiveMissedPings.set(nodeId, missed);
    this.consecutiveHealthyPings.set(nodeId, 0); // Reset recovery streak!

    let nextState: NodeLifecycleState = node.lifecycleState;

    if (node.lifecycleState === 'RECOVERING') {
      // Invariant: any single missed heartbeat while RECOVERING drops back to OFFLINE immediately
      nextState = 'OFFLINE';
      await this.store.appendEvent({
        eventId: `evt-recov-drop-${Date.now()}`,
        kind: 'node.offline',
        payload: { nodeId, reason: 'Missed heartbeat during recovery phase' },
        originId: 'heartbeat',
        sequence: 0,
        occurredAt: new Date(now).toISOString(),
      });
    } else if (node.lifecycleState === 'ONLINE' && missed >= this.heartbeatPolicy.degradedAfterMissed && missed < this.heartbeatPolicy.offlineAfterMissed) {
      nextState = 'DEGRADED';
      await this.store.appendEvent({
        eventId: `evt-degraded-${Date.now()}`,
        kind: 'node.degraded',
        payload: { nodeId, missedPings: missed },
        originId: 'heartbeat',
        sequence: 0,
        occurredAt: new Date(now).toISOString(),
      });
    } else if (missed >= this.heartbeatPolicy.offlineAfterMissed && node.lifecycleState !== 'OFFLINE') {
      nextState = 'OFFLINE';
      await this.store.appendEvent({
        eventId: `evt-offline-${Date.now()}`,
        kind: 'node.offline',
        payload: { nodeId, missedPings: missed },
        originId: 'heartbeat',
        sequence: 0,
        occurredAt: new Date(now).toISOString(),
      });
    }

    if (nextState !== node.lifecycleState) {
      const updated: NodeCapability = {
        ...node,
        lifecycleState: nextState,
      };
      await this.store.saveNodeCapability(updated);
      return updated;
    }

    return node;
  }

  /**
   * Sets node administrative lifecycle state (DRAINING, DISABLED, etc.)
   */
  async setNodeLifecycle(nodeId: string, state: NodeLifecycleState): Promise<void> {
    const node = await this.getNode(nodeId);
    if (!node) return;

    const updated: NodeCapability = {
      ...node,
      lifecycleState: state,
      policy: {
        ...node.policy,
        draining: state === 'DRAINING',
        disabled: state === 'OFFLINE' ? node.policy.disabled : false,
      },
    };
    await this.store.saveNodeCapability(updated);
  }

  /**
   * Returns concurrency ramp-up multiplier for recovering nodes.
   */
  getCapacityMultiplier(node: NodeCapability, now: number = Date.now()): number {
    if (node.lifecycleState !== 'ONLINE' && node.lifecycleState !== 'RECOVERING') {
      return 0;
    }
    if (!node.recoveryStartedAt) {
      return 1.0;
    }

    const elapsed = now - new Date(node.recoveryStartedAt).getTime();
    let cumulativeMs = 0;

    for (const phase of node.policy.recoveryRampUpPhases) {
      const duration = phase.durationMs === 'infinite' ? Infinity : phase.durationMs;
      if (elapsed < cumulativeMs + duration) {
        return phase.capacityPercent / 100;
      }
      cumulativeMs += duration;
    }

    return 1.0;
  }

  /**
   * Returns all eligible online nodes supporting the requested model kind.
   */
  async getEligibleNodes(kind: ModelKind): Promise<readonly NodeCapability[]> {
    const nodes = await this.store.getNodeCapabilities();
    return nodes.filter(
      (n) =>
        n.lifecycleState === 'ONLINE' &&
        !n.policy.disabled &&
        !n.policy.quarantined &&
        n.supportedModelKinds.includes(kind)
    );
  }
}
